import {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Avatar, Box, Button, Chip, CircularProgress, Stack, Typography} from '@mui/material';
import {
  AccessTimeRounded,
  BusinessRounded,
  GpsFixedRounded,
  LoginRounded,
  LogoutRounded,
  MapRounded,
  OpenInNewRounded,
} from '@mui/icons-material';
import './attendance-record-summary.scss';
import {AttendanceRecordType} from '../../backend/enums/attendance-record-type';
import {translations} from '../../i18n/translations';
import {Pallete} from '../../theme/bluebird';
import {AttendanceLocationMap} from './attendance-location-map';

type AttendanceRecordSummaryProps = {
  /** Entry or exit. While it is not known, a loading indicator is shown if `isTypeLoading` is set. */
  type?: AttendanceRecordType;
  isTypeLoading?: boolean;
  user?: {name?: string; picture?: string};
  entityName?: string;
  /** Date and time, already formatted */
  dateTime?: string;
  /** Time zone of the date and time, when it is worth showing */
  timeZone?: string;
  location?: {latitude: number; longitude: number; accuracy: number};
  /** What is shown in the location while there isn't one */
  locationFallback?: string;
  /** If the map can be moved and zoomed. Fixed maps do not get in the way when scrolling the page. */
  interactiveMap?: boolean;
  mapHeight?: number;
  /** Do not show the professional and the entity, when they are already shown somewhere else */
  hideContext?: boolean;
  /** When set, only the type and this message are shown (e.g. an exit that was not registered yet) */
  emptyMessage?: string;
  /** Shown after the map */
  children?: ReactNode;
};

/** What is registered in an attendance record (or is going to be): who, where, when, and the map of its location */
export const AttendanceRecordSummary = ({
  type,
  isTypeLoading,
  user,
  entityName,
  dateTime,
  timeZone,
  location,
  locationFallback = '-',
  interactiveMap,
  mapHeight,
  emptyMessage,
  hideContext,
  children,
}: AttendanceRecordSummaryProps) => {
  const {t} = useTranslation();
  const texts = translations.daily_care_log.attendance_tracker;
  const isEntry = type === AttendanceRecordType.Entry;

  return (
    <Stack className="attendance-record-summary" gap="16px">
      <Stack className="attendance-record-summary-card" gap="14px">
        <Box className="attendance-record-summary-row">
          <Typography className="label">{t(texts.new.record_type)}</Typography>
          {type ? (
            <Chip
              icon={isEntry ? <LoginRounded /> : <LogoutRounded />}
              label={t(isEntry ? texts.new.entry : texts.new.exit)}
              sx={{
                backgroundColor: isEntry ? Pallete.green.base : Pallete.red.base,
                color: '#fff',
                '& .MuiChip-icon': {color: '#fff'},
              }}
            />
          ) : (
            isTypeLoading && <CircularProgress size={20} />
          )}
        </Box>

        {emptyMessage ? (
          <Typography className="empty-message">{emptyMessage}</Typography>
        ) : (
          <>
            {!hideContext && (
              <>
                <Box className="attendance-record-summary-row">
                  <Typography className="label">{t(texts.new.professional)}</Typography>
                  <Box className="value">
                    <Avatar src={user?.picture} alt={user?.name} sx={{width: 28, height: 28}} />
                    <Typography>{user?.name}</Typography>
                  </Box>
                </Box>

                <Box className="attendance-record-summary-row">
                  <Typography className="label">{t(texts.new.entity)}</Typography>
                  <Box className="value">
                    <BusinessRounded fontSize="small" />
                    <Typography>{entityName}</Typography>
                  </Box>
                </Box>
              </>
            )}

            <Box className="attendance-record-summary-row">
              <Typography className="label">{t(texts.new.date_time)}</Typography>
              <Box className="value">
                <AccessTimeRounded fontSize="small" />
                <Typography>{dateTime}</Typography>
              </Box>
            </Box>

            {timeZone && (
              <Box className="attendance-record-summary-row">
                <Typography className="label">{t(texts.details.time_zone)}</Typography>
                <Box className="value">
                  <Typography>{timeZone}</Typography>
                </Box>
              </Box>
            )}

            <Box className="attendance-record-summary-row">
              <Typography className="label">{t(texts.new.location)}</Typography>
              <Box className="value">
                <GpsFixedRounded fontSize="small" />
                <Typography>
                  {location
                    ? `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`
                    : locationFallback}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Stack>

      {!emptyMessage && location && (
        <>
          <Box className="attendance-record-summary-row">
            <Typography className="label">{t(texts.new.accuracy)}</Typography>
            <Chip label={t(texts.new.accuracy_value, {meters: Math.round(location.accuracy)})} />
          </Box>
          <AttendanceLocationMap
            latitude={location.latitude}
            longitude={location.longitude}
            accuracy={location.accuracy}
            interactive={interactiveMap}
            height={mapHeight}
          />
          {/* So the professional can confirm the location in Google Maps */}
          <Box className="attendance-record-summary-map-link">
            <Button
              variant="outlined"
              size="small"
              startIcon={<MapRounded />}
              endIcon={<OpenInNewRounded />}
              href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(texts.details.open_in_maps)}
            </Button>
          </Box>
        </>
      )}

      {children}
    </Stack>
  );
};
